import React, { useState, useEffect, useCallback } from "react";
import { LoginFormContext, LoginFormContextType } from "../utils";
import { Btn } from "../comps";
import { IconArrowLeft } from "@/components/icon";
import { AuthDataRes, ApplySatus } from "@/types/LoginPage";
import { useTranslation } from "react-i18next";
import { Button, Input, Result, message, Select, Tooltip } from "antd";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { SyncOutlined, EllipsisOutlined } from "@/utils/antdIcon";
import { Avatar } from "@/baseComponents/Avatar";

import styles from "./index.module.less";

export default function ActiveFormComp({
  userData,
  team,
  onSelectTeam
}: {
  userData: AuthDataRes;
  team: any;
  onSelectTeam: any;
}) {
  const { t } = useTranslation("common");
  const { navigate } = React.useContext<LoginFormContextType>(LoginFormContext);
  const { service } = React.useContext<GlobalContextConfig>(GlobalContext);
  const [reason, setReason] = useState("");
  const [adminUserInfo, setAdminUserInfo] = useState({
    userId: "",
    teamId: "",
    avatar: "",
    deptName: "",
    realName: ""
  });
  const [status, setStatus] = useState<ApplySatus>(ApplySatus.NOT_APPLY);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const goBack = useCallback(() => {
    setReason("");
    if (userData.teamUserInfoList.length === 1) {
      navigate("step1");
    } else {
      navigate("stepNewTeam");
    }
  }, [navigate, userData]);

  const getStatus = useCallback(
    (status?: ApplySatus) => {
      service.authService
        .unEnableApprStatus({
          "x-token": userData.accessToken,
          "x-team-id": team.teamId
        })
        .then((res) => {
          setLoaded(true);
          setStatus(status || res.apprStatus);
          res.manageUserId &&
            setAdminUserInfo({
              userId: res.manageUserId,
              teamId: res.manageTeamId,
              avatar: res.manageHeadImg,
              deptName: res.manageFullDeptName,
              realName: res.manageRealName
            });
          setRejectReason(res.apprOption);
        })
        .catch((e) => {
          goBack();
          console.error(e.msg, e.debugMsg);
        });
    },
    [service.authService, userData, team, goBack]
  );

  useEffect(() => {
    getStatus();
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, team, userData]);

  const reloadStatus = () => {
    // 获取审核状态
    setLoaded(false);
    setRejectReason("");
    getStatus();
  };
  const onReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
  };
  const apply = () => {
    setLoading(true);
    service.authService
      .applyAccountEXP(reason, {
        "x-token": userData.accessToken,
        "x-team-id": team.teamId
      })
      .then(() => {
        getStatus();
      })
      .catch((e) => {
        console.error(e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const remindAdmin = () => {
    service.authService
      .reminderApprUnEnable({
        "x-token": userData.accessToken,
        "x-team-id": team.teamId
      })
      .then(() => {
        message.success("已提醒管理者审核，请稍候");
      })
      .catch((e) => {
        console.error(e.toString());
      });
  };

  const ApplyForm = (
    <div className={styles.content}>
      <div className={styles.desc}>
        您的账号已过有效期，当前已被停用，如需继续使用，请向管理者申请延期，或申请变更管理者。
      </div>
      <div className={styles['apply-form']}>
        <div className={styles.item}>
          <div className={styles.label}>申请类型</div>
          <div className={styles.value}>
            <div className={styles.title}>账号延期</div>
            <div className={styles['obile-tips']}>*如需变更管理者，请到移动端操作</div>
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.label}>管理者</div>
          <div className={styles.value}>
            <div className={styles['user-item']}>
              <Avatar src={adminUserInfo.avatar} name={adminUserInfo.realName} />
              <div className={styles.info}>
                <div className={styles.name}>{adminUserInfo.realName}</div>
                <Tooltip title={adminUserInfo.deptName}>
                  <div className={styles['dept-info']}>{adminUserInfo.deptName}</div>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
        <div className={styles['reason-item']}>
          <div className={styles.label}>申请原因</div>
          <div className={styles.value}>
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 3 }}
              maxLength={200}
              style={{ resize: "none", border: "none" }}
              placeholder="请输入申请延期账号的理由"
              onChange={onReasonChange}
              value={reason}
            />
            <div className={styles.count}>{reason.length}/200</div>
          </div>
        </div>
      </div>
      <div className={styles.btns}>
        <Btn text="提交申请" loading={loading} disabled={!reason} onClick={apply} />
        <Button type="text" onClick={goBack}>
          暂不申请
        </Button>
      </div>
    </div>
  );
  const ApplyedForm = (
    <>
      <div className={styles.content}>
        <div className={styles.desc}>您已提交申请，请等待管理者审核</div>
        <div className={`${styles['apply-form']} ${styles['wide-form']}`}>
          <div className={styles.item}>
            <div className={styles.label}> 管理者： </div>
            <div className={styles.value}>{adminUserInfo.realName}</div>
          </div>
          <div className={styles.item}>
            <div className={styles.label}> 管理者部门： </div>
            <div className={styles.value}>{adminUserInfo.deptName}</div>
          </div>
        </div>
        <div className={styles.result}>
          <Result
            // className={`result-${status}`}
            className={styles[`result-${status}`]}
            title="申请审核中"
            icon={
              <div className={styles['applying-icon']}>
                <EllipsisOutlined />
              </div>
            }
          />
          <Button type="text" onClick={reloadStatus}>
            刷新
            <SyncOutlined />
          </Button>
        </div>
      </div>
      <div className={styles.btns}>
        <Btn text="已申请，审核中" disabled={true} />
        <Button type="text" onClick={remindAdmin}>
          提醒管理者审核
        </Button>
      </div>
    </>
  );
  const RejectForm = (
    <>
      <div className={styles.content}>
        <div className={styles.desc}>
          您的申请被驳回{rejectReason ? `，原因是“${rejectReason}”` : ""}。
        </div>
        <div className={`${styles["apply-form"]} ${styles["wide-form"]}`}>
          <div className={styles.item}>
            <div className={styles.label}> 管理者： </div>
            <div className={styles.value}>{adminUserInfo.realName}</div>
          </div>
          <div className={styles.item}>
            <div className={styles.label}> 管理者部门： </div>
            <div className={styles.value}>{adminUserInfo.deptName}</div>
          </div>
        </div>
        <div className={styles.result}>
          <Result status="error" title="申请被驳回" />
        </div>
      </div>
      <div className={styles.btns}>
        <Btn
          text="重新申请"
          onClick={() => {
            getStatus(ApplySatus.NOT_APPLY);
          }}
        />
      </div>
    </>
  );
  const ApprovedForm = (
    <>
      <div className={styles.content}>
        <div className={styles.desc}>您的申请已通过，可登录使用。</div>
        <div className={styles.result}>
          <Result status="success" title="申请已通过" />
        </div>
      </div>
      <div className={styles.btns}>
        <Btn
          text="直接进入"
          onClick={() => {
            onSelectTeam(team.teamId, userData);
          }}
        />
      </div>
    </>
  );
  const getApplyContent = (applyStatus: ApplySatus) => {
    switch (applyStatus) {
      case ApplySatus.NOT_APPLY:
        return ApplyForm;
      case ApplySatus.APPLYING:
        return ApplyedForm;
      case ApplySatus.APPROVE:
        return ApprovedForm;
      case ApplySatus.REJECT:
        return RejectForm;
      default:
        return null;
    }
  };
  return (
    <div className={styles["active-form"]}>
      <div
        style={{
          margin: "10px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        }}
      >
        <div
          className={styles["hover-color"]}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer"
          }}
          onClick={goBack}
        >
          {IconArrowLeft}
          <span
            style={{
              verticalAlign: "middle",
              marginLeft: 5
            }}
          >
            {t("login.back")}
          </span>
        </div>
        <div className={styles["active-title"]}>申请</div>
      </div>
      {loaded && getApplyContent(status)}
    </div>
  )
}
